import React, { useState, useEffect } from "react";
import CitationChips from "./CitationChips.jsx";

/**
 * The scene plan for a section play-through (contract v3.4 §12.4).
 *
 * One row per scene: drag handle, editable topic, PE's one-line summary,
 * "drawn from" chips. The coordinator can reorder (drag, or the arrows),
 * rename a topic, remove a scene, or add one — a topic plus a line of what it
 * should cover. Added scenes show "from your note".
 *
 * Nothing is sent until "Save plan" or "Write scenes"; the latter saves first.
 *
 * props:
 *   plan            {scenes: [{scene_id, topic, summary, origin, citations}], edited}
 *   citations       {scene_id: {citations: [...]}} — server-annotated, for the chips
 *   focusSection    the section being played (to mark scenes that stray)
 *   busy            disable everything (a request is in flight)
 *   hasScript       a script already exists — writing again replaces it
 *   onSave(scenes)  → Promise<bool>; scenes in the PUT /plan shape
 *   onWrite()       → start writing the scenes from the saved plan
 *   onSuggestAgain()
 */
export default function PlanEditor({ plan, citations, focusSection, busy, hasScript,
                                     onSave, onWrite, onSuggestAgain }) {
  const [rows, setRows] = useState(() => toRows(plan));
  const [adding, setAdding] = useState(false);
  const [newTopic, setNewTopic] = useState("");
  const [newSummary, setNewSummary] = useState("");
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  useEffect(() => { setRows(toRows(plan)); }, [plan]);

  const saved = toRows(plan);
  const dirty = JSON.stringify(rows.map(strip)) !== JSON.stringify(saved.map(strip));
  const canSave = rows.length > 0 && rows.every(r => r.topic.trim() && (r.scene_id || r.summary.trim()));

  function move(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
  }
  function drop(from, to) {
    setDragFrom(null); setDragOver(null);
    if (!Number.isInteger(from) || from === to) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRows(next);
  }
  function update(i, patch) {
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function remove(i) {
    if (rows[i]?.kind === "support" && !window.confirm(
      "Remove the “what's hard, and how to help” scene? Caregivers would then get no guidance on this in this section.")) return;
    setRows(rows.filter((_, idx) => idx !== i));
  }
  const gap = (plan?.warnings || []).some(w => w?.code === "support_gap") && !rows.some(r => r.kind === "support");
  function addScene() {
    if (!newTopic.trim() || !newSummary.trim()) return;
    setRows([...rows, { key: `new-${Date.now()}`, scene_id: null, topic: newTopic.trim(),
                        summary: newSummary.trim(), origin: "coordinator", citations: [] }]);
    setNewTopic(""); setNewSummary(""); setAdding(false);
  }

  async function save() {
    return onSave(rows.map(r => r.scene_id
      ? { scene_id: r.scene_id, topic: r.topic.trim(), ...(r.origin === "coordinator" ? { summary: r.summary.trim() } : {}) }
      : { topic: r.topic.trim(), summary: r.summary.trim() }));
  }
  async function write() {
    if (hasScript && !window.confirm("Re-author the scenes from this plan? The current script is replaced — take a snapshot first if you want to keep it.")) return;
    if (dirty && !(await save())) return;
    onWrite();
  }
  function suggestAgain() {
    if ((plan?.edited || dirty) && !window.confirm("Ask for a new plan? Your changes to this plan will be replaced.")) return;
    onSuggestAgain();
  }

  return (
    <div style={pe.wrap}>
      {gap && (
        <p style={pe.gap} role="note">
          Nothing in the sources says what's hard for this person here, or how staff can help — so
          there's no scene about it. An interview on this section could fill the gap.
        </p>
      )}
      <ol style={pe.list} aria-label="Scenes, drag to reorder">
        {rows.map((r, i) => (
          <li key={r.key}
              draggable={!busy}
              onDragStart={e => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); setDragFrom(i); }}
              onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
              onDragOver={e => { e.preventDefault(); if (dragOver !== i) setDragOver(i); }}
              onDrop={e => { e.preventDefault(); drop(dragFrom ?? Number(e.dataTransfer.getData("text/plain")), i); }}
              style={{ ...pe.row, ...(dragFrom === i ? pe.dragging : {}),
                       ...(dragOver === i && dragFrom !== null && dragFrom !== i ? pe.over : {}) }}>
            <span style={pe.handle} aria-hidden="true">⋮⋮</span>
            <span style={pe.num}>{i + 1}</span>
            <div style={pe.body}>
              {r.kind === "support" && <span style={pe.kind}>What's hard, and how to help</span>}
              <input style={pe.topic} value={r.topic} disabled={busy} maxLength={120}
                     aria-label={`Scene ${i + 1} topic`}
                     onChange={e => update(i, { topic: e.target.value })} />
              {r.origin === "coordinator"
                ? <input style={pe.summaryInput} value={r.summary} disabled={busy} maxLength={400}
                         aria-label={`Scene ${i + 1}: what it should cover`}
                         placeholder="One line of what this scene should cover"
                         onChange={e => update(i, { summary: e.target.value })} />
                : r.summary && <p style={pe.summary}>{r.summary}</p>}
              <CitationChips citations={(citations?.[r.scene_id] || {}).citations || r.citations}
                             focusSection={focusSection} origin={r.origin} />
            </div>
            <div style={pe.tools}>
              <button type="button" style={pe.icon} disabled={busy || i === 0} onClick={() => move(i, -1)} aria-label={`Move scene ${i + 1} up`}>↑</button>
              <button type="button" style={pe.icon} disabled={busy || i === rows.length - 1} onClick={() => move(i, 1)} aria-label={`Move scene ${i + 1} down`}>↓</button>
              <button type="button" style={pe.icon} disabled={busy} onClick={() => remove(i)} aria-label={`Remove scene ${i + 1}`}>×</button>
            </div>
          </li>
        ))}
      </ol>

      {adding ? (
        <div style={pe.addBox}>
          <input style={pe.topic} value={newTopic} placeholder="Topic, e.g. “Her drum”" maxLength={120}
                 aria-label="New scene topic" onChange={e => setNewTopic(e.target.value)} autoFocus />
          <input style={pe.summaryInput} value={newSummary} maxLength={400}
                 placeholder="One line of what it should cover" aria-label="New scene: what it should cover"
                 onChange={e => setNewSummary(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && addScene()} />
          <div style={pe.btnRow}>
            <button type="button" style={pe.secondary} disabled={!newTopic.trim() || !newSummary.trim()} onClick={addScene}>Add</button>
            <button type="button" style={pe.ghost} onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" style={pe.ghostAccent} disabled={busy} onClick={() => setAdding(true)}>+ Add a scene</button>
      )}

      <div style={pe.footer}>
        <button type="button" style={pe.ghost} disabled={busy} onClick={suggestAgain}>Re-plan</button>
        <div style={{ flex: 1 }} />
        {dirty && <button type="button" style={pe.secondary} disabled={busy || !canSave} onClick={save}>Save plan</button>}
        <button type="button" style={{ ...pe.primary, ...((busy || !canSave) ? pe.muted : {}) }}
                disabled={busy || !canSave} onClick={write}>Author scenes</button>
      </div>
    </div>
  );
}

function toRows(plan) {
  return (plan?.scenes || []).map(s => ({
    key: s.scene_id, scene_id: s.scene_id, topic: s.topic || "", summary: s.summary || "",
    origin: s.origin || "pe", citations: s.citations || [], kind: s.kind || null,
  }));
}
function strip(r) { return [r.scene_id, r.topic, r.summary]; }

const pe = {
  wrap:   { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  gap:    { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "var(--color-bg-subtle)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-3)" },
  kind:   { alignSelf: "flex-start", fontSize: 11, fontWeight: "var(--weight-semibold)", color: "var(--color-accent)", background: "var(--color-accent-subtle)", borderRadius: 999, padding: "1px 8px", marginBottom: 4 },
  list:   { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  row:    { display: "flex", alignItems: "flex-start", gap: "var(--space-2)", background: "var(--color-bg-base)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-3)", transition: "transform 120ms ease, opacity 120ms ease" },
  dragging: { opacity: 0.4 },
  over:   { borderColor: "var(--color-accent)" },
  handle: { cursor: "grab", color: "var(--color-text-tertiary)", fontSize: 12, lineHeight: "28px", userSelect: "none" },
  num:    { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-tertiary)", lineHeight: "28px", minWidth: 16 },
  body:   { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  topic:  { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-1) var(--space-2)", background: "var(--color-surface)", fontFamily: "inherit", width: "100%", boxSizing: "border-box" },
  summary:{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0, lineHeight: "var(--leading-normal)" },
  summaryInput: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-1) var(--space-2)", background: "var(--color-surface)", fontFamily: "inherit", width: "100%", boxSizing: "border-box" },
  tools:  { display: "flex", gap: 2 },
  icon:   { width: 28, height: 28, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", background: "var(--color-surface)", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, lineHeight: 1, padding: 0 },
  addBox: { display: "flex", flexDirection: "column", gap: "var(--space-2)", border: "1px dashed var(--color-border-strong)", borderRadius: "var(--radius-md)", padding: "var(--space-3)" },
  btnRow: { display: "flex", gap: "var(--space-2)" },
  footer: { display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap", borderTop: "1px solid var(--color-border)", paddingTop: "var(--space-3)" },
  primary:   { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "#fff", background: "var(--color-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  secondary: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  ghost:     { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", background: "none", border: "none", cursor: "pointer", padding: "var(--space-2)" },
  ghostAccent: { alignSelf: "flex-start", fontSize: "var(--text-sm)", color: "var(--color-accent)", background: "none", border: "none", cursor: "pointer", padding: "var(--space-1) 0" },
  muted:     { opacity: 0.45, cursor: "not-allowed" },
};
